/**
 * Scan Engine Context
 *
 * Provides the unified scan engine to the component tree.
 * Place <ScanEngineProvider> inside BrowserRouter so the engine
 * can use useLocation for route-change resets.
 *
 * Renders a fixed-position overlay for the scan focus ring,
 * driven entirely by React state (no DOM classList manipulation).
 */

import React, { createContext, useContext, useMemo, useState, useLayoutEffect, useEffect, useRef, useCallback } from 'react';
import { useScanEngine, ScanEngineState, FocusOverlay } from '@/hooks/useScanEngine';
import { useTranslation } from '@/hooks/useTranslation';

interface ScanEngineContextValue {
  isActive: boolean;
  refreshItems: () => void;
  focusDefaultItem: () => boolean;
}

const ScanEngineContext = createContext<ScanEngineContextValue>({
  isActive: false,
  refreshItems: () => {},
  focusDefaultItem: () => false,
});

// ── Focus Overlay Component ────────────────────────────────────────────────

interface OverlayRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

function ScanFocusOverlay({ overlay }: { overlay: FocusOverlay | null }) {
  const [rects, setRects] = useState<OverlayRect[]>([]);
  const rafRef = useRef<number>(0);

  // Read rects once synchronously when overlay changes
  useLayoutEffect(() => {
    if (!overlay || overlay.elements.length === 0) {
      setRects([]);
      return;
    }

    const newRects = overlay.elements
      .map(el => {
        const r = el.getBoundingClientRect();
        return { left: r.left, top: r.top, width: r.width, height: r.height };
      })
      .filter(r => r.width > 0 && r.height > 0);

    setRects(newRects);
  }, [overlay]);

  // rAF loop: re-read rects every frame so overlay follows scrolling/animations
  useEffect(() => {
    if (!overlay || overlay.elements.length === 0) return;

    const tick = () => {
      const newRects = overlay.elements
        .map(el => {
          const r = el.getBoundingClientRect();
          return { left: r.left, top: r.top, width: r.width, height: r.height };
        })
        .filter(r => r.width > 0 && r.height > 0);

      setRects(prev => {
        // Only update if rects actually changed to avoid unnecessary re-renders
        if (prev.length !== newRects.length) return newRects;
        for (let i = 0; i < prev.length; i++) {
          if (prev[i].left !== newRects[i].left || prev[i].top !== newRects[i].top ||
              prev[i].width !== newRects[i].width || prev[i].height !== newRects[i].height) {
            return newRects;
          }
        }
        return prev;
      });

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [overlay]);

  if (rects.length === 0) return null;

  const isItem = overlay?.type === 'item';

  // Check if any focused element has an orange-ish background (where orange scan focus is invisible)
  const hasTealOverride = (() => {
    // First check explicit data-cursor-color attribute
    const hasExplicit = overlay?.elements?.some(
      el => el.getAttribute('data-cursor-color') === 'teal' ||
            el.closest?.('[data-cursor-color="teal"]')
    ) ?? false;
    if (hasExplicit) return true;

    // Then check computed background color for orange hues
    try {
      return overlay?.elements?.some(el => {
        const bg = window.getComputedStyle(el).backgroundColor;
        const match = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (!match) return false;
        const r = parseInt(match[1]), g = parseInt(match[2]), b = parseInt(match[3]);
        // Detect warm hues (orange, peach, coral, salmon): R dominant
        return r > 180 && g > 80 && g < 230 && b < 215 && r > g;
      }) ?? false;
    } catch { return false; }
  })();

  return (
    <>
      {rects.map((rect, i) => (
        <div
          key={i}
          className={[
            isItem ? 'scan-focus-overlay' : 'scan-row-focus-overlay',
            hasTealOverride ? 'scan-focus-teal' : ''
          ].filter(Boolean).join(' ')}
          style={{
            position: 'fixed',
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            pointerEvents: 'none',
            zIndex: 9999,
            borderRadius: '0.75rem',
          }}
        />
      ))}
    </>
  );
}

// ── Provider ───────────────────────────────────────────────────────────────

export function ScanEngineProvider({ children }: { children: React.ReactNode }) {
  const { isRTL } = useTranslation();
  const engine = useScanEngine(isRTL);

  // Memoize context value to prevent child re-renders on focus changes
  const contextValue = useMemo(() => ({
    isActive: engine.isActive,
    refreshItems: engine.refreshItems,
    focusDefaultItem: engine.focusDefaultItem,
  }), [engine.isActive, engine.refreshItems, engine.focusDefaultItem]);

  return (
    <ScanEngineContext.Provider value={contextValue}>
      {children}
      <ScanFocusOverlay overlay={engine.focusOverlay} />
    </ScanEngineContext.Provider>
  );
}

/** Read the scan engine state. Safe to call even when the engine is dormant. */
export function useScanEngineContext(): ScanEngineContextValue {
  return useContext(ScanEngineContext);
}
